import { useEffect, useState } from 'react';
import { MapPin, Clock, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ActiveVisit } from '@/api/visits';

interface ActiveVisitBannerProps {
  visit: ActiveVisit;
  onCheckOut: () => void;
  isCheckingOut?: boolean;
  onClick?: () => void;
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
}

export function ActiveVisitBanner({
  visit,
  onCheckOut,
  isCheckingOut,
  onClick,
}: ActiveVisitBannerProps) {
  // Real-time duration update
  const [duration, setDuration] = useState(visit?.durationMinutes ?? 0);

  useEffect(() => {
    if (!visit?.startedAt) return;

    // Update duration every minute
    const interval = setInterval(() => {
      const startTime = new Date(visit.startedAt).getTime();
      const now = Date.now();
      const diffMinutes = Math.floor((now - startTime) / 60000);
      setDuration(diffMinutes);
    }, 60_000);

    // Initial calculation
    const startTime = new Date(visit.startedAt).getTime();
    const now = Date.now();
    setDuration(Math.floor((now - startTime) / 60000));

    return () => clearInterval(interval);
  }, [visit?.startedAt]);

  // Guard against missing client data
  if (!visit?.client) {
    return null;
  }

  return (
    <div
      className={cn(
        'bg-sky-200 border-b border-sky-300 px-4 py-3 text-gray-900',
        onClick && 'cursor-pointer'
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-3">
        {/* Left: Client info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-sky-700 flex-shrink-0" />
            <span className="font-medium text-sm truncate">
              {visit.displayName || visit.client.name}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-sky-700">
            <Clock className="h-3 w-3" />
            <span>{formatDuration(duration)}</span>
          </div>
        </div>

        {/* Right: Check-out button */}
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onCheckOut();
          }}
          disabled={isCheckingOut}
          className="bg-white hover:bg-gray-50 border-sky-500"
        >
          <LogOut className="h-4 w-4 mr-1.5" />
          {isCheckingOut ? 'Завершаю...' : 'Завершить'}
        </Button>
      </div>
    </div>
  );
}
